import React from 'react';
interface BillHeaderProps {
  company: {
    name: string;
    address: string[];
    phone: string;
    email: string;
  };
  bill: {
    number: string;
    status: 'Submitted' | 'Approved' | 'Paid';
    billDate: string;
    dueDate: string;
    totalDueAmount: number;
    matchedAmount: number;
    outstandingAmount: number;
    createdBy: string;
    lastUpdated: string;
    printedBy: string;
    printingDate: string;
  };
}
export function BillHeader({
  company,
  bill
}: BillHeaderProps) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Paid':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'Approved':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'Submitted':
        return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      default:
        return 'bg-neutral-50 text-neutral-700 border-neutral-200';
    }
  };
  const formatCurrency = (amount: number) => {
    return `$${amount.toFixed(2)}`;
  };
  return <>
      {/* Top Header */}
      <div className="flex justify-between items-start mb-8" data-id="element-46">
        {/* Left: Company Info */}
        <div className="flex-1" data-id="element-47">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg mb-4 flex items-center justify-center shadow-md" data-id="element-48">
            <span className="text-white font-bold text-xl" data-id="element-49">MT</span>
          </div>
          <h1 className="text-xl font-bold text-neutral-900 mb-2" data-id="element-50">
            {company.name}
          </h1>
          <div className="text-xs text-neutral-600 space-y-0.5 leading-relaxed" data-id="element-51">
            {company.address.map((line, i) => <p key={i} data-id="element-52">{line}</p>)}
            <p className="mt-2" data-id="element-53">Tel: {company.phone}</p>
            <p data-id="element-54">Email: {company.email}</p>
          </div>
        </div>

        {/* Right: Bill Metadata Box */}
        <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-5 min-w-[360px] shadow-sm" data-id="element-55">
          <div className="space-y-2" data-id="element-56">
            <div className="flex justify-between items-center" data-id="element-57">
              <span className="text-xs font-medium text-neutral-600" data-id="element-58">
                Bill No.:
              </span>
              <span className="text-xs font-semibold text-neutral-900" data-id="element-59">
                {bill.number}
              </span>
            </div>
            <div className="flex justify-between items-center" data-id="element-60">
              <span className="text-xs font-medium text-neutral-600" data-id="element-61">
                Status:
              </span>
              <span className={`text-xs font-semibold px-2 py-1 rounded border ${getStatusColor(bill.status)}`} data-id="element-62">
                {bill.status}
              </span>
            </div>
            <div className="flex justify-between" data-id="element-63">
              <span className="text-xs font-medium text-neutral-600" data-id="element-64">
                Bill Date:
              </span>
              <span className="text-xs text-neutral-900" data-id="element-65">{bill.billDate}</span>
            </div>
            <div className="flex justify-between" data-id="element-66">
              <span className="text-xs font-medium text-neutral-600" data-id="element-67">
                Due Date:
              </span>
              <span className="text-xs font-semibold text-neutral-900" data-id="element-68">
                {bill.dueDate}
              </span>
            </div>

            <div className="h-px bg-neutral-200 my-2" data-id="element-69" />

            <div className="flex justify-between" data-id="element-70">
              <span className="text-xs font-medium text-neutral-600" data-id="element-71">
                Total Due Amount:
              </span>
              <span className="text-xs font-semibold text-neutral-900 tabular-nums" data-id="element-72">
                {formatCurrency(bill.totalDueAmount)}
              </span>
            </div>
            <div className="flex justify-between" data-id="element-73">
              <span className="text-xs font-medium text-neutral-600" data-id="element-74">
                Matched Amount:
              </span>
              <span className="text-xs text-green-600 tabular-nums" data-id="element-75">
                {formatCurrency(bill.matchedAmount)}
              </span>
            </div>
            <div className="flex justify-between" data-id="element-76">
              <span className="text-xs font-medium text-neutral-600" data-id="element-77">
                Outstanding Amount:
              </span>
              <span className="text-xs font-semibold text-red-600 tabular-nums" data-id="element-78">
                {formatCurrency(bill.outstandingAmount)}
              </span>
            </div>

            <div className="h-px bg-neutral-200 my-2" data-id="element-79" />

            <div className="flex justify-between" data-id="element-80">
              <span className="text-xs font-medium text-neutral-600" data-id="element-81">
                Created By:
              </span>
              <span className="text-xs text-neutral-900" data-id="element-82">{bill.createdBy}</span>
            </div>
            <div className="flex justify-between" data-id="element-83">
              <span className="text-xs font-medium text-neutral-600" data-id="element-84">
                Last Updated:
              </span>
              <span className="text-xs text-neutral-900" data-id="element-85">
                {bill.lastUpdated}
              </span>
            </div>
            <div className="flex justify-between" data-id="element-86">
              <span className="text-xs font-medium text-neutral-600" data-id="element-87">
                Printed By:
              </span>
              <span className="text-xs text-neutral-900" data-id="element-88">{bill.printedBy}</span>
            </div>
            <div className="flex justify-between" data-id="element-89">
              <span className="text-xs font-medium text-neutral-600" data-id="element-90">
                Printing Date:
              </span>
              <span className="text-xs text-neutral-900" data-id="element-91">
                {bill.printingDate}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Centered Title */}
      <div className="text-center mb-6" data-id="element-92">
        <h2 className="text-2xl font-bold text-neutral-900 tracking-tight" data-id="element-93">
          SUPPLIER BILL
        </h2>
      </div>
    </>;
}